import CharacterCount from "@tiptap/extension-character-count";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle as TextStyleExtension } from "@tiptap/extension-text-style";
import {
	type JSONContent,
	type UseEditorOptions,
	useEditor as useTipTapEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { UniqueID } from "@tiptap-pro/extension-unique-id";
import { useContext } from "react";
import { UploadProgressContext } from "@/contexts/UploadProgressContext";
import type { SessionData } from "@/utils/getSession";
import { ButtonExtension } from "./extensions/button";
import { FileExtension } from "./extensions/file-handler";
import { iFrameExtension } from "./extensions/iframe";
import { ImageExtension, ResizableMediExtension } from "./extensions/image";
import { MentionsExtension } from "./extensions/mentions";
import { VideoExtension } from "./extensions/mux-video";
import { PlaceholderExtension } from "./extensions/placeholder";
import { PollExtension } from "./extensions/poll";
import { YoutubeExtension } from "./extensions/youtube";

type EditorFeatures =
	| "placeholder"
	| "colors"
	| "attachments"
	| "video"
	| "poll"
	| "youtube"
	| "image"
	| "iframe"
	| "button"
	| "mention"
	| "styling"
	| "files";

type EditorFeaturesConfig = Partial<
	Record<EditorFeatures, { enabled: boolean }>
>;

export function createEditor({
	editable,
	content,
	features = {},
	club,
	session,
	placeholder,
	placeholderType,
	mimeTypes,
	onAttachmentUpload,
	onUploadProgress,
	size = "base",
}: {
	editable: boolean;
	content?: JSONContent;
	features?: EditorFeaturesConfig;
	club: string;
	session?: Partial<SessionData>;
	placeholder?: string;
	placeholderType?: "full" | "first-line";

	mimeTypes?: string[];

	onAttachmentUpload?: (file: {
		id: string;
		rawID: string;
		name: string;
		signedUrl: string;
		size?: string;
	}) => void;
	onUploadProgress?: (op: { uploadId: string; progress: number }) => void;
	size?: "sm" | "base" | "lg";
}): UseEditorOptions {
	const proseSize =
		size === "sm" ? "prose-sm" : size === "lg" ? "prose-lg" : "prose-base";

	return {
		editable,
		immediatelyRender: false,
		content,
		editorProps: {
			attributes: {
				class: `prose prose-zinc w-full max-w-full min-w-full dark:prose-invert ${proseSize}`,
			},
		},
		extensions: [
			UniqueID.configure({ attributeName: "uid" }),
			CharacterCount,
			StarterKit.configure({
				link: {
					HTMLAttributes: {
						target: "_blank",
						rel: "noopener",
					},
				},
			}),
			TextStyleExtension,
			TextAlign.configure({
				types: ["heading", "paragraph"],
			}),
			...(placeholder
				? [PlaceholderExtension({ placeholder, placeholderType })]
				: []),
			...(features?.image?.enabled
				? [ImageExtension, ResizableMediExtension]
				: []),
			...(features?.colors?.enabled ? [] : []),
			...(features?.button?.enabled ? [ButtonExtension] : []),
			...(features?.youtube?.enabled ? [YoutubeExtension] : []),
			...(features?.mention?.enabled ? [MentionsExtension(club)] : []),
			...(features?.video?.enabled ? [VideoExtension] : []),
			...(features?.poll?.enabled ? [PollExtension] : []),
			...(features?.iframe?.enabled ? [iFrameExtension] : []),
			...(features?.files?.enabled
				? [
						FileExtension({
							onAttachmentUpload,
							onUploadProgress,
							session,
							club,
							mimeTypes,
						}),
					]
				: []),
		],
	};
}

export type CreateEditorParameters = Parameters<typeof createEditor>;

export function useEditor(params: CreateEditorParameters[0]) {
	const uploadContext = useContext(UploadProgressContext);

	return useTipTapEditor(
		createEditor({
			...params,
			onUploadProgress: ({ uploadId, progress }) =>
				uploadContext.setProgress(uploadId, progress || 0),
		}),
	);
}
